import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogIn, ArrowLeft } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { useLocalization } from "../../hooks/useLocalization";
import { LanguageSwitcher } from "../../components/LanguageSwitcher";
import { getLandingRouteForUser } from "../../lib/roleRouting";

export default function LoginPage() {
  const { login, user } = useAuth();
  const { t } = useLocalization();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("yoko_session_expired")) {
      sessionStorage.removeItem("yoko_session_expired");
      setError(t("auth.sessionExpired"));
    }
  }, [t]);

  useEffect(() => {
    if (user) {
      navigate(getLandingRouteForUser(user), { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const result = await login(email, password);
    setSubmitting(false);
    if (result.mfaSetupRequired) {
      navigate("/staff/mfa/setup", { state: { setupToken: result.setupToken, email: result.email } });
    } else if (result.mfaChallengeRequired) {
      navigate("/staff/mfa/verify", { state: { challengeToken: result.challengeToken, email: result.email } });
    } else if (!result.success) {
      setError(result.error || t("auth.invalidCredentials"));
    } else {
      const target = result.user.must_change_password ? "/change-password" : getLandingRouteForUser(result.user);
      navigate(target, { replace: true });
    }
  };

  return (
    <div className="w-full max-w-sm" data-testid="login-page">
      <Link
        to="/"
        data-testid="back-to-home-link"
        className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-600 mb-6 transition-colors"
      >
        <ArrowLeft size={13} />
        {t("auth.backToHome")}
      </Link>
      <div className="flex justify-between items-center mb-8">
        <img src="/yokolicious-wordmark.png" alt="YOKOLICIOUS" className="h-9 lg:hidden" data-testid="login-page-logo" />
        <LanguageSwitcher />
      </div>
      <div className="flex items-center gap-3 mb-1">
        <img src="/yokolicious-eagle-salute.png" alt="" className="h-12 w-12 object-contain drop-shadow-sm" data-testid="login-mascot" />
        <h2 className="text-3xl font-bold tracking-tight text-gray-900">{t("homepage.staffLogin")}</h2>
      </div>
      <p className="text-sm text-gray-500 mb-8">{t("auth.signInToContinue")}</p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="login-email" className="text-sm font-medium text-gray-700 mb-1 block">{t("auth.email")}</label>
          <input
            id="login-email"
            data-testid="login-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          />
        </div>
        <div>
          <label htmlFor="login-password" className="text-sm font-medium text-gray-700 mb-1 block">{t("auth.password")}</label>
          <input
            id="login-password"
            data-testid="login-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          />
        </div>
        {error && <p className="text-sm text-red-500" data-testid="login-error">{error}</p>}
        <div className="flex justify-end">
          <Link to="/forgot-password" className="text-xs font-medium text-blue-600 hover:underline" data-testid="forgot-password-link">
            {t("auth.forgotPassword")}
          </Link>
        </div>
        <button
          data-testid="login-submit-button"
          type="submit"
          disabled={submitting}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-full bg-blue-600 text-white font-semibold hover:bg-blue-700 hover:-translate-y-[1px] active:scale-95 transition-all disabled:opacity-50"
        >
          <LogIn size={16} />
          {submitting ? t("common.loading") : t("auth.login")}
        </button>
      </form>
    </div>
  );
}
